import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, IconButton, LinearProgress, Tooltip, Typography } from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopyOutlined';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import ReactPlayer from 'react-player';
import videoUploads from '../../../services/videoUploads';
import { UploadCancelled, uploadMultipart } from './multipartUpload';
import xhrPutPart from './xhrPutPart';
import { VideoTranscodeState } from '../../../api';
import sessionsService from '../../../services/sessions';
import { refreshSession } from '../../Util/useSession';
import { timeSpanToSeconds, Video } from '../../../services/sessionModels';
import { transcodeState as videoTranscodeState } from '../../../services/sessionsUpload';
import AreYouSure from '../../Util/AreYouSure';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import { checkVideo, videoCheckInfo, videoProblems } from './VideoStatus';
import { Mono, StepPill, StepState } from '../../Runs/RunStatusUi';
import uploadErrorMessage from './uploadErrorMessage';
import importErrorMessage from './importErrorMessage';

export type SlotVideo = Video;

interface Props {
  sessionId: number;
  sortOrder: number;
  label: string;
  video?: SlotVideo;
  onUploaded: () => void;
  onRemove?: () => void;
  onDeleted: () => void;
  initialFile?: File;
  base?: { fps?: number | null; width?: number | null; height?: number | null };
}

const transcodeState = (video: SlotVideo): StepState => {
  switch (videoTranscodeState(video)) {
    case VideoTranscodeState.Succeeded:
      return 'done';
    case VideoTranscodeState.Failed:
      return 'failed';
    case VideoTranscodeState.Running:
    case VideoTranscodeState.Copying:
      return 'running';
    default:
      return 'waiting';
  }
};

const formatDuration = (seconds: number) => {
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(h ? 2 : 1, '0');
  return `${h ? h + ':' : ''}${mm}:${String(s).padStart(2, '0')}`;
};

const videoFacts = (video: SlotVideo) => {
  const duration = timeSpanToSeconds(video.length);
  return [
    video.width && video.height ? `${video.width}×${video.height}` : '',
    video.fps ? `${Math.round(video.fps * 100) / 100} fps` : '',
    duration ? formatDuration(duration) : '',
    video.encoding ? video.encoding.toUpperCase() : '',
    video.bitrate ? `${(video.bitrate / 1000000).toFixed(1)} Mbit/s` : '',
  ].filter(Boolean);
};

type Phase = 'idle' | 'uploading' | 'done' | 'error';

export const accept = '.mp4,.mkv,.ts,.avi,.mov';

export const formatBytes = (bytes: number) => {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
  }
  return `${Math.round(bytes / 1024 / 1024)} MB`;
};

const SessionVideoR2UploadSlot: React.FunctionComponent<Props> = ({
  sessionId,
  sortOrder,
  label,
  video,
  onUploaded,
  onRemove,
  onDeleted,
  initialFile,
  base,
}) => {
  const [file, setFile] = useState<File>();
  const [phase, setPhase] = useState<Phase>('idle');
  const [uploaded, setUploaded] = useState(0);
  const [total, setTotal] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [message, setMessage] = useState<string>();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const controller = useRef<AbortController>();
  const sample = useRef({ time: 0, bytes: 0 });

  useEffect(() => {
    if (phase !== 'uploading') {
      return;
    }
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [phase]);

  useEffect(() => () => controller.current?.abort(), []);

  const onProgress = useCallback((bytes: number, size: number) => {
    const now = Date.now();
    const elapsed = (now - sample.current.time) / 1000;
    if (elapsed >= 1) {
      setSpeed((previous) => {
        const current = Math.max(0, (bytes - sample.current.bytes) / elapsed);
        return previous ? previous * 0.7 + current * 0.3 : current;
      });
      sample.current = { time: now, bytes };
    }
    setUploaded(bytes);
    setTotal(size);
  }, []);

  const start = useCallback(
    async (selected: File) => {
      const abort = new AbortController();
      controller.current = abort;
      sample.current = { time: Date.now(), bytes: 0 };
      setFile(selected);
      setPhase('uploading');
      setMessage(undefined);
      setSpeed(0);
      setUploaded(0);
      setTotal(selected.size);

      try {
        await uploadMultipart({
          file: selected,
          sessionId,
          sortOrder,
          api: videoUploads,
          putPart: xhrPutPart,
          signal: abort.signal,
          onProgress,
        });
        setPhase('done');
        setMessage('Uploaded. The video is probed and transcoding has started.');
        onUploaded();
      } catch (error) {
        if (error instanceof UploadCancelled) {
          setPhase('idle');
          setUploaded(0);
        } else {
          setPhase('error');
        }
        setMessage(uploadErrorMessage(error));
      } finally {
        if (controller.current === abort) {
          controller.current = undefined;
        }
      }
    },
    [sessionId, sortOrder, onProgress, onUploaded]
  );

  const startedInitial = useRef(false);
  useEffect(() => {
    if (initialFile && !startedInitial.current) {
      startedInitial.current = true;
      start(initialFile);
    }
  }, [initialFile, start]);

  const onPick = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selected = event.target.files?.[0];
      event.target.value = '';
      if (selected) {
        start(selected);
      }
    },
    [start]
  );

  const cancel = useCallback(() => controller.current?.abort(), []);

  const remove = useCallback(async () => {
    setConfirmDelete(false);
    if (!video) {
      return;
    }
    setDeleting(true);
    try {
      await sessionsService.deleteVideo(video.id);
      await refreshSession(sessionId);
    } finally {
      setDeleting(false);
    }
    setFile(undefined);
    setPhase('idle');
    setMessage(undefined);
    onDeleted();
  }, [video, sessionId, onDeleted]);

  const importing = video?.importState === 'IMPORTING';

  useEffect(() => {
    if (!importing) return undefined;
    const timer = setInterval(() => refreshSession(sessionId), 10000);
    return () => clearInterval(timer);
  }, [importing, sessionId]);

  const retry = useCallback(() => {
    if (file) {
      start(file);
    }
  }, [file, start]);

  const [copied, setCopied] = useState(false);
  const copyUrl = useCallback(() => {
    if (!video?.url) return;
    navigator.clipboard.writeText(video.url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [video]);

  const percentage = total > 0 ? (100 * uploaded) / total : 0;
  const currentFile = video?.url?.split('/').pop();
  const uploading = phase === 'uploading';
  const facts = video ? videoFacts(video) : [];
  const problems = video?.width
    ? videoProblems(checkVideo(videoCheckInfo(video), base?.fps || undefined, base?.width || undefined, base?.height || undefined))
    : undefined;

  return (
    <Slot empty={!currentFile && !file}>
      <Header>
        <Typography variant="subtitle1">{label}</Typography>
        {video && !uploading && <StepPill state={transcodeState(video)} />}
      </Header>

      {currentFile && !uploading && video && videoTranscodeState(video) === VideoTranscodeState.Succeeded && video.transcodeStatus?.streamUrl && (
        <Preview>
          <ReactPlayer url={video.transcodeStatus.streamUrl} controls muted width="100%" height="100%" />
        </Preview>
      )}

      {currentFile && !uploading ? (
        <Current>
          <Url>
            <Mono>{video?.url}</Mono>
            <Tooltip title={copied ? 'Copied' : 'Copy URL'}>
              <IconButton size="small" onClick={copyUrl}>
                <FileCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Url>
          {video?.importState === 'IMPORTING' && <Facts>Downloading from the link...</Facts>}
          {video?.importState === 'FAILED' && <ErrorText>{importErrorMessage(video.importErrorCode)}</ErrorText>}
          {facts.length > 0 && <Facts>{facts.join(' · ')}</Facts>}
          {problems?.errors.map((problem) => (
            <Problem key={problem} error>
              <ErrorIcon fontSize="inherit" />
              {problem}
            </Problem>
          ))}
          {problems?.warnings.map((problem) => (
            <Problem key={problem}>
              <WarningIcon fontSize="inherit" />
              {problem}
            </Problem>
          ))}
          {video?.transcodeStatus?.error && <ErrorText>{video.transcodeStatus.error}</ErrorText>}
        </Current>
      ) : (
        <Typography variant="body2" color="textSecondary">
          {file?.name || 'No video yet'}
        </Typography>
      )}

      {(uploading || phase === 'error' || (phase === 'done' && total > 0)) && (
        <Progress>
          <LinearProgress variant="determinate" value={percentage} color={phase === 'error' ? 'secondary' : 'primary'} />
          <Typography variant="body2" color="textSecondary">
            {Math.floor(percentage)}% · {formatBytes(uploaded)} of {formatBytes(total)}
            {uploading && speed > 0 ? ` · ${formatBytes(speed)}/s` : ''}
          </Typography>
        </Progress>
      )}

      {message && (
        <Typography variant="body2" color={phase === 'error' ? 'error' : 'textSecondary'}>
          {message}
        </Typography>
      )}

      <Actions>
        <Button variant="outlined" color="primary" component="label" disabled={uploading} startIcon={<CloudUploadIcon />}>
          {currentFile ? 'Replace video' : 'Choose video'}
          <input type="file" accept={accept} hidden onChange={onPick} />
        </Button>
        {phase === 'error' && file && (
          <Button variant="contained" color="primary" onClick={retry}>
            Retry
          </Button>
        )}
        {uploading && (
          <Button color="secondary" onClick={cancel}>
            Cancel upload
          </Button>
        )}
        {video && !uploading && (
          <Button onClick={() => setConfirmDelete(true)} disabled={deleting}>
            Delete video
          </Button>
        )}
        {onRemove && !video && !uploading && <Button onClick={onRemove}>Remove camera</Button>}
      </Actions>
      <AreYouSure
        open={confirmDelete}
        title={`Delete the ${label.toLowerCase()} video?`}
        text="The video is removed from this session. The file in storage is kept."
        onYes={remove}
        onNo={() => setConfirmDelete(false)}
      />
    </Slot>
  );
};

const Slot = styled.div<{ empty: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: ${({ empty }) => (empty ? '#fff' : '#f8fafc')};
  ${({ empty }) =>
    empty &&
    `
    justify-content: center;
    align-items: center;
    text-align: center;
    min-height: 160px;
  `}
  border: 1px ${({ empty }) => (empty ? 'dashed #cbd5e1' : 'solid #e2e8f0')};
  border-radius: 8px;
`;

const Preview = styled.div`
  aspect-ratio: 16 / 9;
  background: #0f172a;
  border-radius: 6px;
  overflow: hidden;
  video {
    object-fit: contain;
  }
`;

const Current = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  ${Mono} {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Facts = styled.div`
  color: #64748b;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
`;

const Problem = styled.div<{ error?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 13px;
  color: ${({ error }) => (error ? '#b91c1c' : '#b45309')};
  svg {
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

const ErrorText = styled.div`
  color: #b91c1c;
  font-size: 13px;
  overflow-wrap: anywhere;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const Progress = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const Url = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  word-break: break-all;
`;

export default SessionVideoR2UploadSlot;
